import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Plus, Boxes, Calculator, Filter, Search } from 'lucide-react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import { loadsAPI, recipesAPI, firearmsAPI, uploadsAPI, firingSessionsAPI } from '../services/api';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import { formatLocalDate } from '../services/date';

// Debounce helper for event handlers
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const Loads = () => {
  const [loads, setLoads] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [caseBatches, setCaseBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [calculatedCost, setCalculatedCost] = useState(null);
  const [firearms, setFirearms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    recipe_name: '',
    firearm_manufacturer: '',
    firearm_model: '',
    firearm_caliber: '',
    cartridge: '',
    bullet_brand: '',
    bullet_model: '',
    bullet_weight: '',
    bullet_core_material: '',
    bullet_covering: '',
    bullet_shape: '',
    primer_brand: '',
    primer_model: '',
    primer_firearm_type: '',
    primer_size: '',
    primer_magnum: '',
    powder_brand: '',
    powder_model: '',
    brass_brand: '',
    brass_head_stamp: '',
    brass_caliber: ''
  });
  const [sortBy, setSortBy] = useState('remaining');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddLoadsModal, setShowAddLoadsModal] = useState(false);
  const [showFiringSessionModal, setShowFiringSessionModal] = useState(false);
  const [selectedLoadForAdd, setSelectedLoadForAdd] = useState(null);
  const [selectedLoadForSession, setSelectedLoadForSession] = useState(null);
  const [imagesByLoad, setImagesByLoad] = useState({});
  const [filePreview, setFilePreview] = useState(null);
  const [createLoadFiles, setCreateLoadFiles] = useState([]);
  const [createLoadPreviews, setCreateLoadPreviews] = useState([]);
  // Modal-local file selections
  const [addLoadFilesModal, setAddLoadFilesModal] = useState([]);
  const [addLoadPreviewsModal, setAddLoadPreviewsModal] = useState([]);
  const [sessionFilesModal, setSessionFilesModal] = useState([]);
  const [sessionPreviewsModal, setSessionPreviewsModal] = useState([]);
  const [importingSessionVelocities, setImportingSessionVelocities] = useState(false);
  const sessionImportInputRef = useRef(null);
  const hasAutoCollapsedRef = useRef(false);

  // Build previews for files selected in the Create Load form
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      try {
        const files = Array.from(createLoadFiles || []);
        const results = await Promise.all(files.map(async (f) => {
          const name = f.name || '';
          const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
          const isPdf = /\.pdf$/i.test(name);
          const isDocLike = /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|json|md|log)$/i.test(name);
          if (isImage || isPdf) {
            const url = URL.createObjectURL(f);
            return { url, filename: name, file: f, docThumb: false, revoke: true };
          }
          if (isDocLike) {
            try {
              const res = await uploadsAPI.thumbnailPreview(f);
              const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: 'image/png' });
              const url = URL.createObjectURL(blob);
              return { url, filename: name, file: f, docThumb: true, revoke: true };
            } catch {
              // fallback to local blob url
              const url = URL.createObjectURL(f);
              return { url, filename: name, file: f, docThumb: false, revoke: true };
            }
          }
          // default
          const url = URL.createObjectURL(f);
          return { url, filename: name, file: f, docThumb: false, revoke: true };
        }));
        if (!cancelled) setCreateLoadPreviews(results);
      } catch {
        if (!cancelled) setCreateLoadPreviews([]);
      }
    };
    build();
    return () => {
      cancelled = true;
      try {
        (createLoadPreviews || []).forEach(p => { if (p && p.revoke && p.url) URL.revokeObjectURL(p.url); });
      } catch {}
    };
  }, [createLoadFiles]);

  // Build previews for files selected in the Add Loads modal
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      try {
        const files = Array.from(addLoadFilesModal || []);
        const results = await Promise.all(files.map(async (f) => {
          const name = f.name || '';
          const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
          const isPdf = /\.pdf$/i.test(name);
          const isDocLike = /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|json|md|log)$/i.test(name);
          if (isImage || isPdf) {
            const url = URL.createObjectURL(f);
            return { url, filename: name, file: f, docThumb: false, revoke: true };
          }
          if (isDocLike) {
            try {
              const res = await uploadsAPI.thumbnailPreview(f);
              const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: 'image/png' });
              const url = URL.createObjectURL(blob);
              return { url, filename: name, file: f, docThumb: true, revoke: true };
            } catch {
              const url = URL.createObjectURL(f);
              return { url, filename: name, file: f, docThumb: false, revoke: true };
            }
          }
          const url = URL.createObjectURL(f);
          return { url, filename: name, file: f, docThumb: false, revoke: true };
        }));
        if (!cancelled) setAddLoadPreviewsModal(results);
      } catch {
        if (!cancelled) setAddLoadPreviewsModal([]);
      }
    };
    build();
    return () => {
      cancelled = true;
      try {
        (addLoadPreviewsModal || []).forEach(p => { if (p && p.revoke && p.url) URL.revokeObjectURL(p.url); });
      } catch {}
    };
  }, [addLoadFilesModal]);

  // Build previews for files selected in the Firing Session modal
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      try {
        const files = Array.from(sessionFilesModal || []);
        const results = await Promise.all(files.map(async (f) => {
          const name = f.name || '';
          const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
          const isPdf = /\.pdf$/i.test(name);
          const isDocLike = /\.(docx?|xlsx?|pptx?|odt|ods|odp|rtf|txt|csv|json|md|log)$/i.test(name);
          if (isImage || isPdf) {
            const url = URL.createObjectURL(f);
            return { url, filename: name, file: f, docThumb: false, revoke: true };
          }
          if (isDocLike) {
            try {
              const res = await uploadsAPI.thumbnailPreview(f);
              const blob = res?.data instanceof Blob ? res.data : new Blob([res.data], { type: 'image/png' });
              const url = URL.createObjectURL(blob);
              return { url, filename: name, file: f, docThumb: true, revoke: true };
            } catch {
              const url = URL.createObjectURL(f);
              return { url, filename: name, file: f, docThumb: false, revoke: true };
            }
          }
          const url = URL.createObjectURL(f);
          return { url, filename: name, file: f, docThumb: false, revoke: true };
        }));
        if (!cancelled) setSessionPreviewsModal(results);
      } catch {
        if (!cancelled) setSessionPreviewsModal([]);
      }
    };
    build();
    return () => {
      cancelled = true;
      try {
        (sessionPreviewsModal || []).forEach(p => { if (p && p.revoke && p.url) URL.revokeObjectURL(p.url); });
      } catch {}
    };
  }, [sessionFilesModal]);

  const removeCreatePreview = (idx) => {
    setCreateLoadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Track deleted recipes by id for reliable lookup
  const deletedRecipeIds = useMemo(() => {
    try {
      return new Set((recipes || []).filter(r => r && r.is_deleted).map(r => r.id));
    } catch {
      return new Set();
    }
  }, [recipes]);
  
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  const watchRecipeId = watch('recipe_id');
  const watchQuantity = watch('quantity_created');

  // Inline firing session modal form
  const { register: registerSession, handleSubmit: handleSubmitSession, control: controlSession, reset: resetSession, formState: { errors: errorsSession }, getValues: getSessionValues } = useForm({
    defaultValues: { velocities: [] }
  });
  const { fields: velocityFields, append: appendVelocity, remove: removeVelocity, replace: replaceVelocities } = useFieldArray({
    control: controlSession,
    name: 'velocities'
  });
  const addVelocityField = () => appendVelocity({ velocity: '' });
  const removeVelocityField = (index) => removeVelocity(index);

  const sessionVelocityValues = useWatch({ control: controlSession, name: 'velocities' }) || [];
  const hasSessionVelocities = sessionVelocityValues.length > 0;
  const enteredSessionVelocityCount = sessionVelocityValues.reduce((count, entry) => {
    const num = Number(entry?.velocity);
    return Number.isFinite(num) ? count + 1 : count;
  }, 0);
  const minSessionRounds = Math.max(1, enteredSessionVelocityCount);
  const sessionMaxRounds = selectedLoadForSession && Number.isFinite(Number(selectedLoadForSession.remaining_quantity))
    ? Number(selectedLoadForSession.remaining_quantity)
    : null;
  const sessionHasMax = Number.isFinite(sessionMaxRounds);
  const sessionRoundsPlaceholder = sessionHasMax
    ? `Min ${minSessionRounds} • Max ${sessionMaxRounds}`
    : `Min ${minSessionRounds}`;

  const applyImportedSessionVelocities = (values) => {
    if (!Array.isArray(values) || values.length === 0) {
      return 0;
    }
    const sanitized = values
      .map((value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return null;
        return { velocity: `${Math.round(num)}` };
      })
      .filter(Boolean);
    if (sanitized.length === 0) {
      return 0;
    }
    const current = getSessionValues('velocities') || [];
    replaceVelocities([...current, ...sanitized]);
    return sanitized.length;
  };

  const handleSessionVelocityImport = async (files) => {
    const importFiles = Array.from(files || []).filter(Boolean);
    if (importFiles.length === 0) return;
    setImportingSessionVelocities(true);
    let totalImported = 0;
    let hadError = false;
    for (const file of importFiles) {
      try {
        const res = await firingSessionsAPI.importVelocities(file);
        const parsed = Array.isArray(res?.data?.velocities) ? res.data.velocities : [];
        totalImported += applyImportedSessionVelocities(parsed);
      } catch (error) {
        hadError = true;
        console.error('Failed to import velocities', error);
        const message = error?.response?.data?.error || `Failed to import ${file.name}`;
        toast.error(message);
      }
    }
    // Show success message if any velocities were imported (even if some files failed)
    if (totalImported > 0) {
      toast.success(`Imported ${totalImported} velocities`);
    } else if (!hadError) {
      // Only show "no velocities found" if there were no errors (all files processed but none had data)
      toast.error('No velocities were found under SPEED (FPS).');
    }
    setImportingSessionVelocities(false);
  };

  const handleSessionImportChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;
    await handleSessionVelocityImport(files);
    setSessionFilesModal((prev) => [...prev, ...files]);
  };

  const handleClearSessionVelocities = () => {
    replaceVelocities([]);
    toast.success('Velocities cleared');
  };

  useEffect(() => {
    loadData();
  }, []);
  // Listen for uploads changes to refresh loads/images when new files are added elsewhere
  useEffect(() => {
    const handler = (e) => {
      try {
        if (e?.detail?.entity === 'load') {
          loadData();
        }
      } catch {}
    };
    window.addEventListener('uploadsChanged', handler);
    return () => window.removeEventListener('uploadsChanged', handler);
  }, []);
  // Incremental update handler for firing session changes
  const handleFiringSessionChange = useCallback(async (event) => {
    const detail = event?.detail || {};
    try {
      // Only refresh loads (not everything) - much faster
      const loadsRes = await loadsAPI.getAll();
      const newLoads = Array.isArray(loadsRes.data) ? loadsRes.data : [];
      setLoads(newLoads);
      
      // Refresh images for the affected load if we know which one
      if (detail.loadId) {
        try {
          const imgRes = await uploadsAPI.list('load', detail.loadId);
          setImagesByLoad(prev => ({
            ...prev,
            [detail.loadId]: Array.isArray(imgRes.data) ? imgRes.data : []
          }));
        } catch {}
      }
    } catch (error) {
      console.error('Error handling firing session change:', error);
    }
  }, []);

  // Debounced version to prevent rapid-fire updates
  const debouncedSessionChangeRef = useRef(null);
  useEffect(() => {
    debouncedSessionChangeRef.current = debounce(handleFiringSessionChange, 300);
  }, [handleFiringSessionChange]);

  useEffect(() => {
    const handler = (event) => {
      if (debouncedSessionChangeRef.current) {
        debouncedSessionChangeRef.current(event);
      }
    };
    window.addEventListener('firingSessionChanged', handler);
    return () => window.removeEventListener('firingSessionChanged', handler);
  }, []);

  const calculateCost = useCallback(async (recipeId, quantity) => {
    try {
      const response = await recipesAPI.getCost(recipeId);
      const costPerRound = response.data.total_cost_per_round;
      const totalCost = costPerRound * quantity;
      
      setCalculatedCost({
        costPerRound,
        totalCost
      });
      
  setValue('cost_per_round', costPerRound.toFixed(2));
      setValue('total_cost', totalCost.toFixed(2));
    } catch (error) {
      console.error('Error calculating cost:', error);
      setCalculatedCost(null);
    }
  }, [setValue]);

  useEffect(() => {
    if (watchRecipeId && watchQuantity) {
      calculateCost(watchRecipeId, watchQuantity);
    }
  }, [watchRecipeId, watchQuantity, calculateCost]);

  // Load case batches when recipe changes
  useEffect(() => {
    if (watchRecipeId) {
      loadCaseBatches(watchRecipeId);
    } else {
      setCaseBatches([]);
    }
  }, [watchRecipeId]);

  useEffect(() => {
    if (!selectedLoadForAdd) return;
    if (selectedLoadForAdd.recipe?.id) {
      setValue('recipe_id', String(selectedLoadForAdd.recipe.id));
    }
    const batchId = selectedLoadForAdd.case_batch?.id ?? selectedLoadForAdd.case_batch_id;
    if (batchId) {
      setValue('case_batch_id', String(batchId));
    }
  }, [selectedLoadForAdd, setValue]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [loadsRes, recipesRes, firearmsRes] = await Promise.all([
        loadsAPI.getAll(),
        recipesAPI.getAll(),
        firearmsAPI.getAll()
      ]);
      setLoads(loadsRes.data);
      setRecipes(recipesRes.data);
      setFirearms(firearmsRes.data);
      // load images per grouped load (combine images across all batch_ids)
      try {
        const map = {};
        for (const l of (loadsRes.data || [])) {
          const ids = Array.isArray(l.batch_ids) && l.batch_ids.length > 0 ? l.batch_ids : [l.id];
          try {
            const lists = await Promise.all(ids.map(id =>
              uploadsAPI.list('load', id).then(r => Array.isArray(r?.data) ? r.data : []).catch(() => [])
            ));
            // merge and dedupe by image id
            const merged = [];
            const seen = new Set();
            lists.flat().forEach(img => { if (img && !seen.has(img.id)) { seen.add(img.id); merged.push(img); } });
            map[l.id] = merged;
          } catch {
            map[l.id] = [];
          }
        }
        setImagesByLoad(map);
      } catch {}
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLoad = async (loadId, files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    try {
      await uploadsAPI.upload('load', loadId, arr);
      const res = await uploadsAPI.list('load', loadId);
      setImagesByLoad(prev => ({ ...prev, [loadId]: res.data }));
      toast.success('Images uploaded');
      // Notify Recipes page to refresh images
      try {
        const load = loads.find(l => String(l.id) === String(loadId));
        const recipeId = load?.recipe?.id || load?.recipe_id;
        if (recipeId) {
          window.dispatchEvent(new CustomEvent('uploadsChanged', {
            detail: { entity: 'load', id: loadId, recipeId }
          }));
        }
      } catch {}
    } catch {
      toast.error('Failed to upload images');
    }
  };

  const removeLoadImage = async (loadId, imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImagesByLoad(prev => ({ ...prev, [loadId]: (prev[loadId] || []).filter(i => i.id !== imageId) }));
      toast.success('Image deleted');
    } catch {
      toast.error('Failed to delete image');
    }
  };

  const loadCaseBatches = async (recipeId) => {
    try {
      const response = await loadsAPI.getCaseBatches(recipeId);
      setCaseBatches(response.data);
    } catch (error) {
      console.error('Error loading case batches:', error);
      setCaseBatches([]);
    }
  };

  // Compute average cost per load for a single grouped card (recipe + case batch)
  const getAverageCostPerLoadForCard = (groupedLoad) => {
    try {
      const tc = Number(groupedLoad.total_cost);
      const qty = Number(groupedLoad.quantity_created);
      if (!isNaN(tc) && !isNaN(qty) && qty > 0) return tc / qty;
      // Fallback to current CPR if total_cost is missing
      const cpr = groupedLoad.computed_cost_per_round ?? groupedLoad.cost_per_round;
      return typeof cpr === 'number' ? Number(cpr) : null;
    } catch {
      return null;
    }
  };
      
  const safeLower = (val) => (typeof val === 'string' ? val.toLowerCase() : '');

  // Filtering and sorting functions
  const parseIntendedCalibers = (calibers) => {
    if (!calibers) return [];
    if (Array.isArray(calibers)) return calibers.filter(Boolean);
    try {
      const parsed = JSON.parse(calibers);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const matchesFilter = (load, filterKey, filterValue) => {
    if (!filterValue) return true;
    
    const value = filterValue.toLowerCase();
    const recipe = load.recipe || {};
    const firearm = recipe.firearm || {};
    const bullet = load.bullet || recipe.bullet || {};
    const primer = load.primer || recipe.primer || {};
    const powder = load.powder || recipe.powder || {};
    const brass = load.brass || recipe.brass || {};
    const intendedFirearms = Array.isArray(load.intended_firearms) ? load.intended_firearms : [];
    const firedFirearms = Array.isArray(load.fired_firearms) ? load.fired_firearms : [];
    const allFirearms = [firearm, ...intendedFirearms, ...firedFirearms].filter(Boolean);
    const getCalibers = (f) => {
      const list = [];
      if (f?.caliber) list.push(f.caliber);
      parseIntendedCalibers(f?.intended_calibers).forEach(c => list.push(c));
      return list;
    };
    const firearmMatches = (getter) => allFirearms.some(f => safeLower(getter(f)).includes(value));
    
    switch (filterKey) {
      case 'recipe_name':
        return safeLower(recipe.name).includes(value);
      case 'firearm_manufacturer':
        return firearmMatches(f => f?.manufacturer || '');
      case 'firearm_model':
        return firearmMatches(f => f?.model || '');
      case 'firearm_caliber':
        return allFirearms.some(f => getCalibers(f).some(cal => safeLower(cal).includes(value)));
      case 'cartridge':
        return safeLower(recipe.cartridge || recipe.cartridge_other).includes(value);
      case 'bullet_brand':
        return safeLower(bullet.brand).includes(value);
      case 'bullet_model':
        return safeLower(bullet.model).includes(value);
      case 'bullet_weight':
        return bullet.weight_grains && bullet.weight_grains.toString().includes(value);
      case 'bullet_core_material':
        return safeLower(bullet.core_material).includes(value);
      case 'bullet_covering':
        return safeLower(bullet.covering).includes(value);
      case 'bullet_shape':
        return safeLower(bullet.shape).includes(value);
      case 'primer_brand':
        return safeLower(primer.brand).includes(value);
      case 'primer_model':
        return safeLower(primer.model).includes(value);
      case 'primer_firearm_type':
        return safeLower(primer.firearm_type).includes(value);
      case 'primer_size':
        return safeLower(primer.primer_size).includes(value);
      case 'primer_magnum':
        return primer && primer.magnum === (value === 'true');
      case 'powder_brand':
        return safeLower(powder.brand).includes(value);
      case 'powder_model':
        return safeLower(powder.model).includes(value);
      case 'brass_brand':
        return safeLower(brass.brand).includes(value);
      case 'brass_head_stamp':
        return safeLower(brass.head_stamp).includes(value);
      case 'brass_caliber':
        return safeLower(brass.caliber).includes(value);
      default:
        return true;
    }
  };

  const filterLoads = (loads) => {
    return loads.filter(load => {
      // Basic search
      const searchLower = (searchTerm || '').toLowerCase();
      const recipe = load.recipe || {};
      const firearm = recipe.firearm || {};
      const matchesSearch = 
        safeLower(recipe.name).includes(searchLower) ||
        safeLower(firearm.manufacturer).includes(searchLower) ||
        safeLower(firearm.model).includes(searchLower) ||
        safeLower(firearm.caliber).includes(searchLower) ||
        safeLower(recipe.cartridge || recipe.cartridge_other).includes(searchLower);
      
      // Component-specific filters
      const matchesFilters = Object.entries(filters).every(([key, value]) => 
        matchesFilter(load, key, value)
      );
      
      return matchesSearch && matchesFilters;
    });
  };

  const sortLoads = (loads) => {
    return [...loads].sort((a, b) => {
      switch (sortBy) {
        case 'remaining':
          return b.remaining_quantity - a.remaining_quantity;
        case 'total_created':
          return b.quantity_created - a.quantity_created;
        case 'cost_per_round':
          return a.cost_per_round - b.cost_per_round;
        case 'total_cost':
          return b.total_cost - a.total_cost;
        case 'date_created':
          return new Date(b.date_created) - new Date(a.date_created);
        case 'recipe_name':
          return a.recipe.name.localeCompare(b.recipe.name);
        default:
          return 0;
      }
    });
  };

  const filteredAndSortedLoads = useMemo(() => {
    const filtered = filterLoads(loads);
    return sortLoads(filtered);
  }, [loads, filters, searchTerm, sortBy]);

  const [collapsedRecipes, setCollapsedRecipes] = useState({});
  const groupedLoads = useMemo(() => {
    const map = new Map();
    filteredAndSortedLoads.forEach((l) => {
      const rid = l.recipe?.id || 'unknown';
      if (!map.has(rid)) {
        map.set(rid, { recipe: l.recipe, id: rid, groups: [] });
      }
      map.get(rid).groups.push(l);
    });
    return Array.from(map.values());
  }, [filteredAndSortedLoads]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      recipe_name: '',
      firearm_manufacturer: '',
      firearm_model: '',
      firearm_caliber: '',
      cartridge: '',
      bullet_brand: '',
      bullet_model: '',
      bullet_weight: '',
      bullet_core_material: '',
      bullet_covering: '',
      bullet_shape: '',
      primer_brand: '',
      primer_model: '',
      primer_firearm_type: '',
      primer_size: '',
      primer_magnum: '',
      powder_brand: '',
      powder_model: '',
      brass_brand: '',
      brass_head_stamp: '',
      brass_caliber: ''
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  // Get compatible firearms for a specific load
  const getCompatibleFirearms = (load) => {
    if (!load.recipe.cartridge) return firearms;
    
    return firearms.filter(firearm => {
      // Check if firearm's primary caliber matches
      if (firearm.caliber === load.recipe.cartridge) return true;
      
      // Check if firearm's intended calibers include the recipe cartridge
      if (firearm.intended_calibers) {
        try {
          const intendedCalibers = JSON.parse(firearm.intended_calibers);
          return intendedCalibers.includes(load.recipe.cartridge);
        } catch (e) {
          return false;
        }
      }
      
      return false;
    });
  };

  // Button handlers
  const handleAddLoads = (load) => {
    setSelectedLoadForAdd(load);
    setShowAddLoadsModal(true);
    try {
      // set recipe first so the case batch select renders
      if (load.recipe && load.recipe.id) {
        setValue('recipe_id', String(load.recipe.id));
      }
      loadCaseBatches(load.recipe.id).then(() => {
        setTimeout(() => {
          const batchId = load?.case_batch?.id || load?.case_batch_id;
          if (batchId) {
            setValue('case_batch_id', String(batchId));
          }
        }, 0);
      });
    } catch {}
  };

  const handleFiringSession = (load) => {
    setSelectedLoadForSession(load);
    setShowFiringSessionModal(true);
    // reset session form defaults when opening
    resetSession({
      firearm_id: '',
      location: '',
      session_date: new Date().toISOString().split('T')[0],
      rounds_fired: '',
      temperature: '',
      notes: '',
      velocities: []
    });
  };

  const closeAddLoadsModal = () => {
    setShowAddLoadsModal(false);
    setSelectedLoadForAdd(null);
    setAddLoadFilesModal([]);
    setAddLoadPreviewsModal([]);
  };

  const closeFiringSessionModal = () => {
    setShowFiringSessionModal(false);
    setSelectedLoadForSession(null);
    setSessionFilesModal([]);
    setSessionPreviewsModal([]);
  };

  // Removed frontend validation - let backend handle it

  const onSubmit = async (data) => {
    try {
      const res = await loadsAPI.create({
        ...data,
        quantity_created: parseInt(data.quantity_created),
        cost_per_round: parseFloat(data.cost_per_round),
        total_cost: parseFloat(data.total_cost),
        case_batch_id: data.case_batch_id || null
      });
      // If files were selected via the Upload Files button, upload them to the new load id
      try {
        const newId = res?.data?.id;
        const filesToUpload = (Array.isArray(addLoadFilesModal) && addLoadFilesModal.length > 0)
          ? addLoadFilesModal
          : createLoadFiles;
        if (newId && Array.isArray(filesToUpload) && filesToUpload.length > 0) {
          await uploadsAPI.upload('load', newId, Array.from(filesToUpload));
          // refresh images for this load
          try {
            const list = await uploadsAPI.list('load', newId);
            setImagesByLoad(prev => ({ ...prev, [newId]: Array.isArray(list?.data) ? list.data : [] }));
          } catch {}
          // Notify Recipes page to refresh images
          try {
            const recipeId = data.recipe_id;
            if (recipeId) {
              window.dispatchEvent(new CustomEvent('uploadsChanged', {
                detail: { entity: 'load', id: newId, recipeId }
              }));
            }
          } catch {}
        }
      } catch {}
      toast.success('Load batch created successfully');
      if (showAddLoadsModal) {
        closeAddLoadsModal();
        setAddLoadFilesModal([]);
        setAddLoadPreviewsModal([]);
      } else {
      setShowForm(false);
        setCreateLoadFiles([]);
      reset();
      setCalculatedCost(null);
      }
      // Incremental update: fetch only loads instead of full loadData()
      const loadsRes = await loadsAPI.getAll();
      setLoads(Array.isArray(loadsRes.data) ? loadsRes.data : []);
    } catch (error) {
      console.error('Error creating load:', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create load batch');
      }
    }
  };

  const onSubmitFiringSession = async (data) => {
    try {
      const res = await firingSessionsAPI.create({
        firearm_id: Number(data.firearm_id),
        load_id: Number(selectedLoadForSession.id),
        location: data.location,
        temperature: data.temperature ? Number(data.temperature) : undefined,
        rounds_fired: Number(data.rounds_fired),
        session_date: data.session_date,
        notes: data.notes || '',
        velocities: (data.velocities || [])
          .map(v => (v && v.velocity ? parseFloat(v.velocity) : null))
          .filter(v => v !== null)
      });
      // Upload any modal-selected files to the new session id
      try {
        const newSessionId = res?.data?.id;
        if (newSessionId && Array.isArray(sessionFilesModal) && sessionFilesModal.length > 0) {
          await uploadsAPI.upload('firing_session', newSessionId, Array.from(sessionFilesModal));
          // Notify Recipes page to refresh images
          try {
            window.dispatchEvent(new CustomEvent('uploadsChanged', {
              detail: { entity: 'firing_session', id: newSessionId, recipeId: selectedLoadForSession?.recipe?.id }
            }));
          } catch {}
        }
      } catch {}
      toast.success('Firing session recorded');
      setShowFiringSessionModal(false);
      setSessionFilesModal([]);
      setSessionPreviewsModal([]);
      resetSession({ velocities: [] });
      
      // Incremental update: fetch only loads instead of full loadData()
      const loadsRes = await loadsAPI.getAll();
      setLoads(Array.isArray(loadsRes.data) ? loadsRes.data : []);
      
      // Dispatch event for other pages (Recipes, Firearms, etc.)
      window.dispatchEvent(new CustomEvent('firingSessionChanged', {
        detail: {
          action: 'created',
          sessionId: res?.data?.id,
          loadId: selectedLoadForSession?.id,
          recipeId: selectedLoadForSession?.recipe?.id
        }
      }));
    } catch (e) {
      toast.error('Failed to record firing session');
    }
  };

  const collapseAll = useCallback(() => {
    const next = {};
    groupedLoads.forEach(({ recipe }, idx) => {
      const key = recipe?.id ?? `recipe-${idx}`;
      next[key] = true;
    });
    setCollapsedRecipes(next);
  }, [groupedLoads]);

  const expandAll = useCallback(() => {
    setCollapsedRecipes({});
  }, []);

  useEffect(() => {
    if (hasAutoCollapsedRef.current) return;
    if (!groupedLoads.length) return;
    collapseAll();
    hasAutoCollapsedRef.current = true;
  }, [groupedLoads, collapseAll]);

  const lockedAddRecipeId = selectedLoadForAdd?.recipe?.id ? String(selectedLoadForAdd.recipe.id) : '';
  const recipeFirearm = selectedLoadForAdd?.recipe?.firearm;
  const recipeFirearmLabel = recipeFirearm
    ? [recipeFirearm.manufacturer, recipeFirearm.model].filter(Boolean).join(' ') + (recipeFirearm.caliber ? ` (${recipeFirearm.caliber})` : '')
    : '';
  const lockedAddRecipeLabel = selectedLoadForAdd?.recipe
    ? `${selectedLoadForAdd.recipe.name}${recipeFirearmLabel ? ` — ${recipeFirearmLabel}` : ''}`
    : 'Recipe unavailable';

  const lockedAddCaseBatchId = (() => {
    if (!selectedLoadForAdd) return '';
    const candidate = selectedLoadForAdd.case_batch?.id ?? selectedLoadForAdd.case_batch_id;
    return candidate ? String(candidate) : '';
  })();

  const lockedCaseBatch = (() => {
    if (!selectedLoadForAdd) return null;
    if (lockedAddCaseBatchId) {
      const match = caseBatches.find(batch => String(batch.id) === lockedAddCaseBatchId);
      if (match) return match;
    }
    return selectedLoadForAdd.case_batch || null;
  })();

  const lockedCaseBatchLabel = lockedCaseBatch
    ? `${lockedCaseBatch.head_stamp || lockedCaseBatch.component_name || 'Case batch'} - ${
        typeof lockedCaseBatch.firing_count === 'number' ? lockedCaseBatch.firing_count : '?'
      } firings${
        Number.isFinite(Number(lockedCaseBatch.remaining_quantity))
          ? ` (${lockedCaseBatch.remaining_quantity} remaining)`
          : ''
      }`
    : 'Case batch information unavailable';

  const totalRounds = loads.reduce((sum, load) => sum + (Number(load.quantity_created) || 0), 0);
  const totalCost = loads.reduce((sum, load) => {
    const total = Number(load.total_cost);
    if (!isNaN(total) && total > 0) return sum + total;
    const cpr = Number(load.cost_per_round);
    const qty = Number(load.quantity_created);
    if (!isNaN(cpr) && !isNaN(qty)) return sum + (cpr * qty);
    return sum;
  }, 0);
  const remainingRounds = loads.reduce((sum, load) => sum + load.remaining_quantity, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Load Batches</h1>
          <p className="text-gray-600 mt-2">Track your ammunition load production</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Create Load Batch</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Batches</p>
              <p className="text-2xl font-bold text-blue-600">{loads.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Boxes className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rounds Created</p>
              <p className="text-2xl font-bold text-green-600">{totalRounds.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Calculator className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Remaining Rounds</p>
              <p className="text-2xl font-bold text-orange-600">{remainingRounds.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Boxes className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Investment</p>
              <p className="text-2xl font-bold text-red-600">${totalCost.toFixed(2)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <Calculator className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex justify-end gap-3 flex-wrap">
        <button
          onClick={collapseAll}
          className="btn btn-secondary flex items-center space-x-2"
        >
          <span>Collapse All</span>
        </button>
        <button
          onClick={expandAll}
          className="btn btn-secondary flex items-center space-x-2"
        >
          <span>Expand All</span>
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn btn-secondary flex items-center space-x-2"
        >
          <Filter className="h-4 w-4" />
          <span>Filters & Search</span>
          {(getActiveFilterCount() > 0 || searchTerm) && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {getActiveFilterCount() + (searchTerm ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="card p-6">
          {/* Search and Sort Controls */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search loads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pl-10 w-full"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input"
            >
              <option value="remaining">Sort by Remaining</option>
              <option value="total_created">Sort by Total Created</option>
              <option value="cost_per_round">Sort by Cost per Round</option>
              <option value="total_cost">Sort by Total Cost</option>
              <option value="date_created">Sort by Date Created</option>
              <option value="recipe_name">Sort by Recipe Name</option>
            </select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Recipe Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Recipe Filters</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Recipe Name"
                  value={filters.recipe_name}
                  onChange={(e) => handleFilterChange('recipe_name', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Cartridge"
                  value={filters.cartridge}
                  onChange={(e) => handleFilterChange('cartridge', e.target.value)}
                  className="input text-sm"
                />
              </div>
            </div>

            {/* Firearm Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Firearm Filters</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Manufacturer"
                  value={filters.firearm_manufacturer}
                  onChange={(e) => handleFilterChange('firearm_manufacturer', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Model"
                  value={filters.firearm_model}
                  onChange={(e) => handleFilterChange('firearm_model', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Caliber"
                  value={filters.firearm_caliber}
                  onChange={(e) => handleFilterChange('firearm_caliber', e.target.value)}
                  className="input text-sm"
                />
              </div>
            </div>

            {/* Bullet Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Bullet Filters</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Brand"
                  value={filters.bullet_brand}
                  onChange={(e) => handleFilterChange('bullet_brand', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Model"
                  value={filters.bullet_model}
                  onChange={(e) => handleFilterChange('bullet_model', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Weight (grains)"
                  value={filters.bullet_weight}
                  onChange={(e) => handleFilterChange('bullet_weight', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Core Material"
                  value={filters.bullet_core_material}
                  onChange={(e) => handleFilterChange('bullet_core_material', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Covering"
                  value={filters.bullet_covering}
                  onChange={(e) => handleFilterChange('bullet_covering', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Shape"
                  value={filters.bullet_shape}
                  onChange={(e) => handleFilterChange('bullet_shape', e.target.value)}
                  className="input text-sm"
                />
              </div>
            </div>

            {/* Component Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Other Components</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Primer Brand"
                  value={filters.primer_brand}
                  onChange={(e) => handleFilterChange('primer_brand', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Primer Model"
                  value={filters.primer_model}
                  onChange={(e) => handleFilterChange('primer_model', e.target.value)}
                  className="input text-sm"
                />
                <select
                  value={filters.primer_firearm_type}
                  onChange={(e) => handleFilterChange('primer_firearm_type', e.target.value)}
                  className="input text-sm"
                >
                  <option value="">All Firearm Types</option>
                  <option value="Pistol">Pistol</option>
                  <option value="Rifle">Rifle</option>
                </select>
                <select
                  value={filters.primer_size}
                  onChange={(e) => handleFilterChange('primer_size', e.target.value)}
                  className="input text-sm"
                >
                  <option value="">All Sizes</option>
                  <option value="Large">Large</option>
                  <option value="Small">Small</option>
                </select>
                <select
                  value={filters.primer_magnum}
                  onChange={(e) => handleFilterChange('primer_magnum', e.target.value)}
                  className="input text-sm"
                >
                  <option value="">All Types</option>
                  <option value="true">Magnum</option>
                  <option value="false">Standard</option>
                </select>
                <input
                  type="text"
                  placeholder="Powder Brand"
                  value={filters.powder_brand}
                  onChange={(e) => handleFilterChange('powder_brand', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Powder Model"
                  value={filters.powder_model}
                  onChange={(e) => handleFilterChange('powder_model', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Brass Brand"
                  value={filters.brass_brand}
                  onChange={(e) => handleFilterChange('brass_brand', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Brass Head Stamp"
                  value={filters.brass_head_stamp}
                  onChange={(e) => handleFilterChange('brass_head_stamp', e.target.value)}
                  className="input text-sm"
                />
                <input
                  type="text"
                  placeholder="Brass Caliber"
                  value={filters.brass_caliber}
                  onChange={(e) => handleFilterChange('brass_caliber', e.target.value)}
                  className="input text-sm"
                />
              </div>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="btn btn-secondary"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Create Load Form */}
      {showForm && (
        <div className="card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Create New Load Batch</h3>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="label">Recipe</label>
              <select
                {...register('recipe_id', { required: 'Recipe is required' })}
                className="input"
              >
                <option value="">Select recipe...</option>
                {recipes.map(recipe => (
                  <option key={recipe.id} value={recipe.id}>
                    {recipe.name} - {recipe.firearm ? `${recipe.firearm.manufacturer} ${recipe.firearm.model} (${recipe.firearm.caliber})` : 'No firearm specified'}
                  </option>
                ))}
              </select>
              {errors.recipe_id && (
                <p className="text-red-500 text-sm mt-1">{errors.recipe_id.message}</p>
              )}
            </div>

            {/* Case Batch Selection */}
            {watchRecipeId && caseBatches.length > 0 && (
              <div className="md:col-span-2">
                <label className="label">Case Batch (Required - select existing brass)</label>
                <select
                  {...register('case_batch_id', { required: 'Case batch is required' })}
                  className="input"
                >
                  <option value="">Select case batch...</option>
                  {caseBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {(batch.head_stamp || batch.component_name)} - {batch.firing_count} firings ({batch.remaining_quantity} remaining)
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-1">
                  Select a case batch to use existing brass. Newly purchased brass will show as 0 firings.
                </p>
                {errors.case_batch_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.case_batch_id.message}</p>
                )}
              </div>
            )}

            <div>
              <label className="label">Quantity to Create</label>
              <input
                type="number"
                {...register('quantity_created', { 
                  required: 'Quantity is required',
                  min: { value: 1, message: 'Quantity must be at least 1' }
                })}
                className="input"
                placeholder="e.g., 50"
              />
              {errors.quantity_created && (
                <p className="text-red-500 text-sm mt-1">{errors.quantity_created.message}</p>
              )}
            </div>

            <div>
              <label className="label">Date Created</label>
              <input
                type="date"
                {...register('date_created', { required: 'Date is required' })}
                className="input"
                defaultValue={new Date().toISOString().split('T')[0]}
              />
              {errors.date_created && (
                <p className="text-red-500 text-sm mt-1">{errors.date_created.message}</p>
              )}
            </div>

            {calculatedCost && (
              <div className="md:col-span-2 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Cost Calculation</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">Cost per round:</span>
                    <p className="font-medium text-blue-900">${calculatedCost.costPerRound.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-blue-700">Total cost:</span>
                    <p className="font-medium text-blue-900">${calculatedCost.totalCost.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}

            <input type="hidden" {...register('cost_per_round')} />
            <input type="hidden" {...register('total_cost')} />

            <div className="md:col-span-2">
              <label className="label">Notes</label>
              <textarea
                {...register('notes')}
                className="input"
                rows="3"
                placeholder="Load development notes, performance observations, etc."
              />
            </div>
            <div className="md:col-span-2">
              <label className="btn btn-secondary btn-sm cursor-pointer">
                Upload Files
                <input
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                  className="hidden"
                  onChange={(e) => setCreateLoadFiles(Array.from(e.target.files || []))}
                />
              </label>
              {createLoadFiles.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{createLoadFiles.length} file(s) selected. They will upload after creation.</p>
              )}
              {createLoadPreviews.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {createLoadPreviews.map((p, idx) => {
                    const name = p.filename || '';
                    const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
                    const isPdf = /\.pdf$/i.test(name);
                    const ext = (name.split('.').pop() || '').toUpperCase();
                    return (
                      <div key={idx} className="relative group">
                        {isImage ? (
                          <img
                            src={p.url}
                            alt={name}
                            className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer"
                            onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                          />
                        ) : isPdf ? (
                          <PdfThumbnail
                            blob={p.file}
                            url={p.url}
                            filename={name}
                            sizeClass="w-16 h-16"
                            onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                          />
                        ) : (
                          <div
                            className="w-16 h-16 bg-gray-100 border rounded shadow flex items-center justify-center cursor-pointer overflow-hidden"
                            onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            title={name}
                          >
                            {p.docThumb ? (
                              <img src={p.url} alt={name} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-[10px] text-gray-700 font-medium">{ext || 'FILE'}</span>
                            )}
                          </div>
                        )}
                        <button
                          type="button"
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                          onClick={() => removeCreatePreview(idx)}
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            

            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" className="btn btn-primary">
                Create Load Batch
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loads List (grouped by recipe) */}
      <div className="space-y-6">
        {groupedLoads.map(({ recipe, id, groups }, idx) => {
          const key = id || `recipe-${idx}`;
          const collapsed = collapsedRecipes[key] ?? false;
          return (
            <div key={key} className="bg-white rounded-xl shadow">
              <button
                type="button"
                className={`w-full px-4 py-4 flex items-center justify-between text-left border-b border-gray-100 transition-shadow ${!collapsed ? 'sticky top-0 z-20 bg-white shadow-sm' : ''}`}
                onClick={() =>
                  setCollapsedRecipes((prev) => ({
                    ...prev,
                    [key]: !collapsed,
                  }))
                }
              >
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{recipe?.name || 'Recipe'}</h2>
                  <p className="text-gray-500 text-sm">
                    {recipe?.cartridge || recipe?.cartridge_other || 'No cartridge specified'}
                  </p>
                </div>
                <div className="flex items-center space-x-3 text-sm text-gray-500">
                  <span>
                    <strong>{groups.length}</strong> batch{groups.length === 1 ? '' : 'es'}
                  </span>
                  <span className="text-lg">{collapsed ? '+' : '−'}</span>
                </div>
              </button>
              {!collapsed && (
                <div className="space-y-4 p-4">
                  {groups.map(load => {
              const rid = load?.recipe?.id;
              const isDeleted = !!(load?.recipe?.is_deleted) || (rid ? deletedRecipeIds.has(rid) : false);
              const firingCount = Number(load?.case_batch?.firing_count);
              const firingLabel = Number.isFinite(firingCount)
                ? `${firingCount} firing${firingCount === 1 ? '' : 's'}`
                : 'Unknown';
              return (
          <div key={load.id} className={`card p-6 ${isDeleted ? 'opacity-60 pointer-events-none' : ''}`}>
                <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <Boxes className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <div className="mb-3">
                    <div className="p-2 bg-amber-50 rounded border border-amber-200">
                      <p className="text-lg font-semibold text-amber-800">
                        Case Batch: {firingLabel}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Created:</span>
                      <p className="font-medium">{load.quantity_created.toLocaleString()} rounds</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Remaining:</span>
                      <p className="font-medium">{load.remaining_quantity.toLocaleString()} rounds</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Current cost per load:</span>
                      <p className="font-medium">
                        {(() => {
                          const backendCpr = load.computed_cost_per_round;
                          const backendAvail = load.cost_available !== false; // default true
                          if (backendAvail && backendCpr !== null && typeof backendCpr !== 'undefined') {
                            return `$${Number(backendCpr).toFixed(2)}`;
                          }
                          const cpr = Number(load.cost_per_round);
                          const tc = Number(load.total_cost);
                          const qty = Number(load.quantity_created);
                          const value = !isNaN(cpr) && cpr > 0
                            ? cpr
                            : (!isNaN(tc) && !isNaN(qty) && qty > 0 ? tc / qty : 0);
                          return `$${value.toFixed(2)}`;
                        })()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Average cost per load:</span>
                      <p className="font-medium">
                        {(() => {
                          const avg = getAverageCostPerLoadForCard(load);
                          return avg == null ? 'NA' : `$${Number(avg).toFixed(2)}`;
                        })()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Total cost:</span>
                      <p className="font-medium">
                        {(() => {
                          const tc = Number(load.total_cost);
                          if (!isNaN(tc) && tc > 0) return `$${tc.toFixed(2)}`;
                          const cpr = Number(load.cost_per_round);
                          const qty = Number(load.quantity_created);
                          const value = (!isNaN(cpr) && !isNaN(qty)) ? cpr * qty : 0;
                          return `$${value.toFixed(2)}`;
                        })()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-sm text-gray-500">
                    Created {formatLocalDate(load.date_created)}
                  </div>
                  
                  {load.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{load.notes}</p>
                    </div>
                  )}
                  {/* Images */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <label className="btn btn-secondary btn-sm cursor-pointer">
                        Upload Files
                        <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={(e) => handleUploadLoad(load.id, e.target.files)} />
                      </label>
                </div>
                    <div className="flex gap-2 overflow-x-auto pt-2 pb-1">
                      {(imagesByLoad[load.id] || []).map(img => {
                        const src = img.download_url || img.url;
                        const isPdf = (img.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                        return (
                          <div key={img.id} className="relative group flex-shrink-0 first:ml-0 last:mr-2">
                            {isPdf ? (
                              <PdfThumbnail
                                fileId={img.id}
                                url={src}
                                thumbnailUrl={img.thumbnail_url}
                                filename={img.filename}
                                sizeClass="w-16 h-16"
                                onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })}
                              />
                            ) : (
                              (() => {
                                const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((img.filename || ''));
                                const thumb = isImg ? src : (img.thumbnail_url || src);
                                return (
                                  <img src={thumb} alt={img.filename} className="w-16 h-16 object-cover rounded shadow cursor-pointer" onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })} />
                                );
                              })()
                            )}
                            <button className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100" onClick={() => removeLoadImage(load.id, img.id)}>×</button>
              </div>
                        );
                      })}
            </div>
                  </div>
                  
                  {/* History */}
                  {Array.isArray(load.history) && load.history.length > 0 && (
                    <div className="mt-4">
                      <div className="text-sm font-medium text-gray-900 mb-2">History</div>
                      <div className="space-y-1 text-sm">
                        {load.history.map((ev, idx) => (
                          <div key={idx} className="flex justify-between items-center text-gray-700">
                            <span className={`${ev.type === 'fired' ? 'text-red-600' : 'text-green-600'}`}>
                              {ev.type === 'fired' ? 'Fired' : 'Added'}
                            </span>
                            <span>{formatLocalDate(ev.date)}</span>
                            <span className="font-medium">{ev.quantity.toLocaleString()}</span>
                            {ev.type !== 'fired' && (
                              <span className="text-gray-600">@ ${Number(ev.cost_per_round || 0).toFixed(2)}/rd</span>
                            )}
          </div>
        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  {!isDeleted && (
                    <div className="mt-4 flex space-x-3">
                      <button onClick={() => handleAddLoads(load)} className="btn btn-primary btn-sm">Add Loads</button>
                      <button onClick={() => handleFiringSession(load)} className="btn btn-secondary btn-sm">Firing Session</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
              );
                  })}
                </div>
              )}
            </div>
          );
        })}
        
        {filteredAndSortedLoads.length === 0 && (
          <div className="text-center py-12">
            <Boxes className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No load batches created yet</p>
            <button
              onClick={() => setShowForm(true)}
              className="btn btn-primary mt-4"
            >
              Create Your First Load Batch
            </button>
          </div>
        )}
      </div>

      {/* Add Loads Modal */}
      {showAddLoadsModal && selectedLoadForAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Add More Loads - {selectedLoadForAdd.recipe.name}
              </h3>
              <button
                onClick={closeAddLoadsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="label">Recipe</label>
                <select className="input" value={lockedAddRecipeId} disabled>
                  <option value={lockedAddRecipeId}>{lockedAddRecipeLabel}</option>
                </select>
                <input
                  type="hidden"
                  value={lockedAddRecipeId}
                  readOnly
                  {...register('recipe_id', { required: 'Recipe is required' })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Recipe selection is locked to match the load card you chose.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="label">Case Batch</label>
                <select className="input" value={lockedAddCaseBatchId} disabled>
                  <option value={lockedAddCaseBatchId}>{lockedCaseBatchLabel}</option>
                </select>
                <input
                  type="hidden"
                  value={lockedAddCaseBatchId}
                  readOnly
                  {...register('case_batch_id', { required: 'Case batch is required' })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Case batch is locked so additional rounds stay with the same brass batch.
                </p>
              </div>

              <div>
                <label className="label">Quantity to Create</label>
                <input
                  type="number"
                  {...register('quantity_created', { 
                    required: 'Quantity is required',
                    min: { value: 1, message: 'Quantity must be at least 1' }
                  })}
                  className="input"
                  placeholder="e.g., 50"
                />
              </div>

              <div>
                <label className="label">Date Created</label>
                <input
                  type="date"
                  {...register('date_created', { required: 'Date is required' })}
                  className="input"
                  defaultValue={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea
                  {...register('notes')}
                  className="input"
                  rows="3"
                  placeholder="Load development notes, performance observations, etc."
                />
              </div>
              <div className="md:col-span-2">
                <label className="btn btn-secondary btn-sm cursor-pointer">
                  Upload Files
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                    className="hidden"
                    onChange={(e) => setAddLoadFilesModal(Array.from(e.target.files || []))}
                  />
                </label>
                {Array.isArray(addLoadFilesModal) && addLoadFilesModal.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{addLoadFilesModal.length} file(s) selected. They will upload after creation.</p>
                )}
                {Array.isArray(addLoadPreviewsModal) && addLoadPreviewsModal.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {addLoadPreviewsModal.map((p, idx) => {
                      const name = p.filename || '';
                      const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
                      const isPdf = /\.pdf$/i.test(name);
                      const ext = (name.split('.').pop() || '').toUpperCase();
                      return (
                        <div key={idx} className="relative group">
                          {isImage ? (
                            <img
                              src={p.url}
                              alt={name}
                              className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            />
                          ) : isPdf ? (
                            <PdfThumbnail
                              blob={p.file}
                              url={p.url}
                              filename={name}
                              sizeClass="w-16 h-16"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                            />
                          ) : (
                            <div
                              className="w-16 h-16 bg-gray-100 border rounded shadow flex items-center justify-center cursor-pointer overflow-hidden"
                              onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                              title={name}
                            >
                              {p.docThumb ? (
                                <img src={p.url} alt={name} className="w-full h-full object-contain" />
                              ) : (
                                <span className="text-[10px] text-gray-700 font-medium">{ext || 'FILE'}</span>
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                            onClick={() => setAddLoadFilesModal(prev => prev.filter((_, i) => i !== idx))}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              

              <div className="md:col-span-2 flex space-x-3">
                <button type="submit" className="btn btn-primary">
                  Create Load Batch
                </button>
                <button
                  type="button"
                  onClick={closeAddLoadsModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Firing Session Modal */}
      {showFiringSessionModal && selectedLoadForSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                New Firing Session - {selectedLoadForSession.recipe.name}
              </h3>
              <button
                onClick={closeFiringSessionModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="label">Selected Load</label>
                <div className="p-3 bg-gray-50 rounded border">
                  <p className="font-medium">{selectedLoadForSession.recipe.name}</p>
                  <p className="text-sm text-gray-600">
                    {selectedLoadForSession.remaining_quantity} rounds remaining
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmitSession(onSubmitFiringSession)} className="grid grid-cols-1 gap-4">
                <div>
                  <label className="label">Firearm</label>
                  <select className="input" {...registerSession('firearm_id', { required: 'Firearm is required' })}>
                    <option value="">Select firearm...</option>
                    {getCompatibleFirearms(selectedLoadForSession).map(firearm => (
                      <option key={firearm.id} value={firearm.id}>
                        {firearm.manufacturer} {firearm.model} ({firearm.caliber})
                      </option>
                    ))}
                  </select>
                  {errorsSession.firearm_id && (
                    <p className="text-red-500 text-sm mt-1">{errorsSession.firearm_id.message}</p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Only compatible firearms are shown</p>
                </div>

                <div>
                  <label className="label">Location</label>
                  <input type="text" className="input" placeholder="e.g., Local Range" {...registerSession('location', { required: 'Location is required' })} />
                  {errorsSession.location && (<p className="text-red-500 text-sm mt-1">{errorsSession.location.message}</p>)}
                </div>

                <div>
                  <label className="label">Date</label>
                  <input type="date" className="input" defaultValue={new Date().toISOString().split('T')[0]} {...registerSession('session_date', { required: 'Date is required' })} />
                  {errorsSession.session_date && (<p className="text-red-500 text-sm mt-1">{errorsSession.session_date.message}</p>)}
                </div>

                <div>
                  <label className="label">Rounds Fired</label>
                  <input
                    type="number"
                    className="input"
                    min={minSessionRounds}
                    max={sessionHasMax ? sessionMaxRounds : undefined}
                    placeholder={sessionRoundsPlaceholder}
                    {...registerSession('rounds_fired', {
                      required: 'Rounds fired is required',
                      validate: (value) => {
                        const num = Number(value);
                        if (!Number.isFinite(num)) return 'Enter a valid number';
                        if (num < minSessionRounds) {
                          return `Must be at least ${minSessionRounds} to cover entered velocities`;
                        }
                        if (sessionHasMax && num > sessionMaxRounds) {
                          return `Cannot exceed ${sessionMaxRounds} remaining rounds`;
                        }
                        return true;
                      },
                    })}
                  />
                  {errorsSession.rounds_fired && (<p className="text-red-500 text-sm mt-1">{errorsSession.rounds_fired.message}</p>)}
                </div>

                <div>
                  <label className="label">Temperature (°F)</label>
                  <input type="number" className="input" placeholder="e.g., 72" {...registerSession('temperature')} />
                </div>

                <div className="md:col-span-2">
                  <label className="label">Notes</label>
                  <textarea className="input" rows="3" placeholder="Range session notes, observations, etc." {...registerSession('notes')} />
                </div>
                <div className="md:col-span-2">
                  <label className="btn btn-secondary btn-sm cursor-pointer">
                    Upload Files
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log"
                      className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        setSessionFilesModal((prev) => [...prev, ...files]);
                      }
                      e.target.value = '';
                    }}
                    />
                  </label>
                  {Array.isArray(sessionFilesModal) && sessionFilesModal.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{sessionFilesModal.length} file(s) selected. They will upload after creation.</p>
                  )}
                  {Array.isArray(sessionPreviewsModal) && sessionPreviewsModal.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {sessionPreviewsModal.map((p, idx) => {
                        const name = p.filename || '';
                        const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
                        const isPdf = /\.pdf$/i.test(name);
                        const ext = (name.split('.').pop() || '').toUpperCase();
                        return (
                          <div key={idx} className="relative group">
                            {isImage ? (
                              <img
                                src={p.url}
                                alt={name}
                                className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer"
                                onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                              />
                            ) : isPdf ? (
                              <PdfThumbnail
                                blob={p.file}
                                url={p.url}
                                filename={name}
                                sizeClass="w-16 h-16"
                                onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                              />
                            ) : (
                              <div
                                className="w-16 h-16 bg-gray-100 border rounded shadow flex items-center justify-center cursor-pointer overflow-hidden"
                                onClick={() => setFilePreview({ id: null, url: p.url, filename: name })}
                                title={name}
                              >
                                {p.docThumb ? (
                                  <img src={p.url} alt={name} className="w-full h-full object-contain" />
                                ) : (
                                  <span className="text-[10px] text-gray-700 font-medium">{ext || 'FILE'}</span>
                                )}
                              </div>
                            )}
                            <button
                              type="button"
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-1.5 text-xs opacity-0 group-hover:opacity-100"
                              onClick={() => setSessionFilesModal(prev => prev.filter((_, i) => i !== idx))}
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Velocity Records */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <label className="label">Velocity Records (fps)</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={addVelocityField} className="btn btn-sm btn-primary">
                        <Plus className="w-4 h-4 mr-1" /> Add Velocity
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => sessionImportInputRef.current?.click()}
                        disabled={importingSessionVelocities}
                      >
                        {importingSessionVelocities ? 'Importing…' : 'Import Velocities'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={handleClearSessionVelocities}
                        disabled={!hasSessionVelocities}
                      >
                        Clear Velocities
                      </button>
                      <input
                        type="file"
                        ref={sessionImportInputRef}
                        className="hidden"
                        multiple
                        accept=".csv,.xlsx"
                        onChange={handleSessionImportChange}
                      />
                    </div>
                  </div>
                  {velocityFields.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                      {velocityFields.map((field, idx) => (
                        <div key={field.id} className="relative">
                          <input type="number" className="input text-sm pr-8" placeholder={`Shot ${idx + 1}`} {...registerSession(`velocities.${idx}.velocity`)} />
                          <button type="button" onClick={() => removeVelocityField(idx)} className="absolute right-1 top-1/2 transform -translate-y-1/2 text-red-500 hover:text-red-700" title="Remove this velocity">×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Click "Add Velocity" to record individual shots or use "Import Velocities" with a CSV/XLSX that has a SPEED (FPS) column.</p>
                </div>

                <div className="flex space-x-3">
                  <button type="submit" className="btn btn-primary">Create Firing Session</button>
                  <button type="button" onClick={closeFiringSessionModal} className="btn btn-secondary">Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <ImagePreviewModal id={filePreview?.id} url={filePreview?.url} filename={filePreview?.filename} thumbnailUrl={filePreview?.thumbnailUrl} onClose={() => setFilePreview(null)} />
    </div>
  );
};

export default Loads;